import { Form, Formik, FormikValues } from "formik";
import React, { FC, useEffect, useRef, useState } from "react";
import DatePickerField from "../../../../../shared/components/DatePickerField";
import InputField from "../../../../../shared/components/InputField";
import UIModal from "../../../../../shared/components/UIModal";
import "./instituteLiveClassForm.scss";
import TimePickerField from "../../../../../shared/components/TimePickerField";
import DropdownField from "../../../../../shared/components/DropdownField";
import { LiveClassCreateModel, LiveClassModel } from "../../../../../models/InstituteModel/liveClass.model";
import { MetaModel } from "../../../../../models/CourseModels/SubjectModel/subject.model";
import { LiveClassDurationTypes } from "../../../../../enums/liveClassDurationTypes";
import BranchService from "../../../../../services/InstituteService/branch.service";
import { useParams } from "react-router-dom";
import { BranchMetaModel } from "../../../../../models/InstituteModel/branches.model";
import { deserialize } from "serializr";
import { MetaService } from "../../../../../services/MetaService/meta.service";
import SectionService from "../../../../../services/InstituteService/section.service";
import LiveClassService from "../../../../../services/InstituteService/liveClass.service";
import { LiveClassValidationSchema } from "../../../instituteFormValidation";
import moment from "moment";
import { TreeSelect } from "antd";


interface InstituteLiveClassFormProps {
  visible: boolean;
  closeHandler: () => void;
  data?: LiveClassModel;
  refreshList: () => void;
}

const InstituteLiveClassForm: FC<InstituteLiveClassFormProps> = (props) => {
  const { visible, closeHandler, data, refreshList } = props;
  const [initialValues, setInitialValues] = useState<LiveClassCreateModel>({});
  const [classRooms, setClassRooms] = useState<MetaModel[]>([]);
  const [tutors, setTutors] = useState<MetaModel[]>([]);
  const [subjects, setSubjects] = useState<MetaModel[]>([]);
  const [sections, setSections] = useState<MetaModel[]>([]);
  const [branches, setBranches] = useState<MetaModel[]>([]);
  const [students, setStudents] = useState<any[]>([]);

  const params: any = useParams();
  const formRef = useRef<any>();
  const [loading, setLoading] = useState(false);

  const fetchBranches = () => {
    MetaService.fetchInstituteBranchesMeta(
      params?.instituteId,
      (data) => {
        setBranches(data);
      },
      () => {},
      () => {}
    );
  };

  const fetchTutors = (branchId: number) => {
    MetaService.fetchInstituteTutorsMeta(
      branchId,
      (data) => {
        setTutors(data);
      },
      () => {},
      () => {}
    );
  };

  const fetchSectionSubjects = (sectionIds: number[]) => {
    MetaService.fetchInstituteSectionSubjectsMeta(
      sectionIds,
      (data) => {
        setSubjects(data);
      },
      () => {},
      () => {}
    );
  };

  const fetchSections = (branchClassRoomId: number) => {
    MetaService.fetchInstituteSectionsMeta(
      branchClassRoomId,
      (data) => {
        setSections(data);
      },
      () => {},
      () => {}
    );
  };

  const fetchBranchClassRooms = (branchId: number) => {
    SectionService.fetchBranchClassRooms(
      {
        branch_id: branchId,
      },
      (data) => {
        const meta = deserialize(MetaModel, data);
        setClassRooms(meta as MetaModel[]);
      },
      () => {},
      () => {}
    );
  };

  const handleSubmit = (values: FormikValues) => {
    setLoading(true);
    if (data && data?.id) {
      LiveClassService.updateLiveClass(
        data?.id,
        values,
        () => {
          closeHandler();
          refreshList();
        },
        () => {},
        () => {
          setLoading(false);
        }
      );
    } else {
      LiveClassService.createLiveClass(
        // params?.instituteId,
        values,
        () => {
          closeHandler();
          refreshList();
        },
        () => {},
        () => {
          setLoading(false);
        }
      );
    }
  };

  const fetchSectionStudents = (sectionIds: number[]) => {
      MetaService.fetchSectionStudentsMeta(
        sectionIds,
        (students) => {
          setStudents([
            {
              title: 'Select All',
              value: 'all',
              children: students?.map((value) => ({ value: value?.id, title: `${value?.name} (${value?.email})`, key: value?.id }))
            }
          ]);
        },
        () => { },
        () => { }
      );
    };


  useEffect(() => {
    fetchBranches();
  }, []);


  useEffect(() => {
    if (data) {
      if (data?.classRoom?.branchClassRoomId) fetchSections(data?.classRoom?.branchClassRoomId);
      if(data?.sections) fetchSectionSubjects(data?.sections?.map((section) => section?.id))
      if(data?.branch) fetchTutors(data?.branch?.id)
      if(data?.branch) fetchBranchClassRooms(data?.branch?.id)
      setInitialValues({
        title: data?.title,
        tutorIds: data?.tutors?.filter((tutor) => tutor?.id !== data?.createdTutorId)?.map(value => value?.id),
        startTime: data?.startTime ? moment.utc(data?.startTime)?.format("HH:mm") : undefined,
        date: data?.date,
        branchId: data?.branch?.id,
        classRoomId: data?.classRoom?.branchClassRoomId,
        sectionIds: data?.sections?.map((section) => section?.id),
        subjectId: data?.subject?.id,
        duration: data?.duration,
        studentIds: data?.studentIds
      });
    }
  }, [data]);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      enableReinitialize
      innerRef={formRef}
      validationSchema={LiveClassValidationSchema}
    >
      {({ values, setFieldValue, resetForm, handleSubmit, errors }) => (
        <UIModal
          visible={visible}
          closeModal={closeHandler}
          onClosed={resetForm}
          submitProps={{
            text: data ? "Update" : "Create",
            type: "submit",
            buttonLoading: loading,
            clickHandler: () => {
              handleSubmit();
            },
          }}
          cancelProps={{
            text: "Cancel",
            clickHandler: closeHandler,
          }}
          className="institute-live-class-form"
        >
          <Form placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <div className="institute-live-class-form__header">
              <div className="institute-live-class-form__title">
                {data ? "Update Live Class" : "New Live Class"}
              </div>
            </div>
            <div className="institute-live-class-form__input">
              <div className="institute-live-class-form__label">
                Live Class Title
              </div>
              <InputField
                type="text"
                name="title"
                placeholder="Type Session Name"
              />
            </div>
            <div className="institute-live-class-form__input">
              <div className="institute-live-class-form__label">Date</div>
              <DatePickerField
                name="date"
                placeholder="Select Date"
                value={values?.date}
                format="YYYY-MM-DD"
                onSelect={(date) => {
                  setFieldValue("date", date?.format("YYYY-MM-DD"));
                }}
              />
            </div>
            <div className="institute-live-class-form__input">
              <div className="institute-live-class-form__label">Time</div>
              <TimePickerField
                name="startTime"
                format="HH:mm"
                placeholder="Select Time"
                value={values?.startTime}
                onSelect={(date) => {
                  setFieldValue("startTime", date?.format("HH:mm"));
                }}
              />
            </div>
            <div className="institute-live-class-form__input">
              <div className="institute-live-class-form__label">Duration</div>
              <DropdownField
                name="duration"
                placeholder="Select Duration"
                value={values?.duration}
                options={[
                  {
                    value: LiveClassDurationTypes.MINUTES_30,
                    label: "30 Minutes",
                  },
                  {
                    value: LiveClassDurationTypes.HOURS_1_MINUTES_0,
                    label: "1 Hour",
                  },
                  {
                    value: LiveClassDurationTypes.HOURS_1_MINUTES_30,
                    label: "1 Hour 30 Minutes",
                  },
                  {
                    value: LiveClassDurationTypes.HOURS_2_MINUTES_0,
                    label: "2 Hours",
                  },
                  {
                    value: LiveClassDurationTypes.HOURS_2_MINUTES_30,
                    label: "2 Hours 30 Minutes",
                  },
                  {
                    value: LiveClassDurationTypes.HOURS_3_MINUTES_0,
                    label: "3 Hours",
                  },
                ]}
                showSearch={false}
                optionFilterProp="label"
                onChange={(value) => {
                  setFieldValue("duration", value);
                }}
              />
            </div>
            <div className="institute-live-class-form__input">
              <div className="institute-live-class-form__label">Branch</div>
              <DropdownField
                name="branchId"
                placeholder="Select"
                value={values?.branchId}
                options={branches}
                showSearch={false}
                optionFilterProp="label"
                onChange={(value) => {
                  setFieldValue("branchId", value);
                  setFieldValue("classRoomId", undefined);
                  setFieldValue("sectionIds", undefined);
                  setFieldValue("studentIds", undefined);
                  fetchBranchClassRooms(value);
                  fetchTutors(value);
                }}
              />
            </div>
            <div className="institute-live-class-form__input">
              <div className="institute-live-class-form__label">Class</div>
              <DropdownField
                name="class"
                placeholder="Select"
                value={values?.classRoomId}
                options={classRooms}
                showSearch={false}
                optionFilterProp="label"
                onChange={(value) => {
                  setFieldValue("classRoomId", value);
                  setFieldValue("sectionIds", undefined);
                  setFieldValue("studentIds", undefined);
                  fetchSections(value);
                }}
              />
            </div>
            <div className="institute-live-class-form__input">
              <div className="institute-live-class-form__label">Section</div>
              <DropdownField
                name="sectionIds"
                mode="multiple"
                placeholder="Select"
                value={values?.sectionIds}
                options={sections}
                showSearch={false}
                optionFilterProp="label"
                onChange={(value) => {
                  setFieldValue("sectionIds", value);
                  setFieldValue("studentIds", undefined);
                  fetchSectionSubjects(value);
                  fetchSectionStudents(value);
                }}
              />
            </div>
            <div className="institute-live-class-form__input">
              <div className="institute-live-class-form__label">Students</div>
              <TreeSelect
                placeholder="Select Students"
                value={values?.studentIds}
                treeData={students}
                treeCheckable={true}
                style={{
                  width: "100%",
                  maxHeight: 300,
                  overflow: 'auto'
                }}
                filterTreeNode={(search, item) => {
                  return (typeof item?.title === "string" ? item.title.toLowerCase() : "").indexOf(search.toLowerCase()) >= 0;
                }}
                onChange={(value) => {
                  setFieldValue("studentIds", value);
                }}
              />
            </div>
            <div className="institute-live-class-form__input">
              <div className="institute-live-class-form__label">Teacher</div>
              <DropdownField
                name="tutorIds"
                mode="multiple"
                placeholder="Select"
                value={values?.tutorIds}
                options={tutors}
                showSearch={false}
                optionFilterProp="label"
                onChange={(value) => {
                  setFieldValue("tutorIds", value);
                }}
              />
            </div>
            <div className="institute-live-class-form__input">
              <div className="institute-live-class-form__label">
                Tag Subject
              </div>
              <DropdownField
                name="subjectId"
                placeholder="Select"
                value={values?.subjectId}
                options={subjects}
                showSearch={false}
                optionFilterProp="label"
                onChange={(value) => {
                  setFieldValue("subjectId", value);
                }}
              />
            </div>
          </Form>
        </UIModal>
      )}
    </Formik>
  );
};

export default InstituteLiveClassForm;
